import _ from "lodash";
import React from "react";
import ACLStore from "lib/aclStore";
import update from "immutability-helper";
import {
  GridOutlined,
  PencilLineOutlined,
  EyeOutlined,
  DownloadOutlined,
  DeleteOutlined,
  ListDashedOutlined,
  ListDashedSlantedOutlined,
  MinusCircleOutlined,
  AddCircleOutlined,
} from "@toddle-edu/ds-icons";
import { fontStyle, colors } from "Constants";
import { MULTI_LEVEL } from "Fields/FieldUtils";
import { ENABLE_DYNAMIC_LOCALISATION_OF_TEMPLATE } from "Constants/permissionConstants";
import { getIsCustomDatasetInsightsEnabled } from "PlanningInsights/modules/Utils";

export const getActionsOptions = ({
  t,
  isDatasetCustomisationEnabled,
  isDeleteDisabled,
  isDownloadDisabled,
  areOperationsDisabled,
  isIncludedInInsights,
}) => {
  const options = [
    {
      key: "PREVIEW_PLANNER_ELEMENT_SET",
      label: t("common:view_and_edit"),
      icon: <EyeOutlined />,
    },
    {
      key: "EDIT_PLANNER_ELEMENT_SET_NAME",
      label: t("common:edit_with_label", {
        label: t("common:name"),
      }),
      disabled: areOperationsDisabled,
      icon: <PencilLineOutlined />,
    },
    ...[
      !isIncludedInInsights
        ? {
            key: "INCLUDE_EXCLUDE_IN_INSIGHTS",
            label: t("planningInsights:include_in_insights"),
            icon: <AddCircleOutlined />,
          }
        : {
            key: "INCLUDE_EXCLUDE_IN_INSIGHTS",
            label: t("planningInsights:remove_from_insights"),
            icon: <MinusCircleOutlined />,
          },
    ],
    {
      key: "DOWNLOAD_PLANNER_ELEMENT_SET",
      label: t("common:download"),
      icon: <DownloadOutlined />,
      disabled: isDownloadDisabled,
    },
    {
      key: "DELETE_PLANNER_ELEMENT_SET",
      label: t("common:delete"),
      icon: <DeleteOutlined />,
      isDestructive: true,
      disabled: isDeleteDisabled || areOperationsDisabled,
      disabledTooltip: isDeleteDisabled
        ? t("common:data_set_delete_disabled_message")
        : null,
    },
  ];

  const optionsToFilterOut = [];

  if (!isDatasetCustomisationEnabled) {
    optionsToFilterOut.push(
      "EDIT_PLANNER_ELEMENT_SET_NAME",
      "DELETE_PLANNER_ELEMENT_SET"
    );
  }

  const isCustomDatasetsPlanningInsightsEnabled = getIsCustomDatasetInsightsEnabled();
  if (!isCustomDatasetsPlanningInsightsEnabled) {
    optionsToFilterOut.push("INCLUDE_EXCLUDE_IN_INSIGHTS");
  }

  return _.filter(options, ({ key }) => !_.includes(optionsToFilterOut, key));
};

export const getPlannerElementSetColumnList = ({ t }) => {
  const isCustomDatasetsPlanningInsightsEnabled = getIsCustomDatasetInsightsEnabled();

  const includedInInsightsColumn = isCustomDatasetsPlanningInsightsEnabled
    ? [
        {
          id: "INCLUDED_IN_INSIGHTS",
          label: t("planningInsights:include_in_insights"),
          type: "CUSTOM",
        },
      ]
    : [];

  return [
    {
      id: "SET_NAME",
      label: t("common:data_set_name"),
      type: "CUSTOM",
      style: {
        width: "300px",
      },
      entity: "LABEL",
      isSticky: true,
    },
    {
      id: "UI_TYPE",
      label: t("unitPlan:ui_type"),
      type: "CUSTOM",
      style: {
        width: "180px",
      },
      entity: "UI_TYPE",
    },
    {
      id: "USED_IN_FIELD",
      label: t("common:used_in_field"),
      type: "CUSTOM",
    },
    {
      id: "USED_IN_TEMPLATE",
      label: t("common:used_in_template"),
      type: "CUSTOM",
    },
    {
      id: "CONFIGURATION_STATUS",
      label: t("common:configuration_status"),
      type: "CUSTOM",
    },
    ...includedInInsightsColumn,
    {
      id: "ACTIONS",
      label: t("common:action_plural"),
      type: "CUSTOM",
      alignment: "center",
    },
  ];
};

export const getPlannerElementSetUiTypeOptions = ({ t }) => {
  return [
    {
      key: "CARD",
      value: "CARD",
      label: t("common:cards"),
      description: t("common:card_ui_type_description"),
      Icon: GridOutlined,
    },
    {
      key: "CHECKLIST",
      value: "CHECKLIST",
      label: t("common:checklist"),
      description: t("common:checklist_ui_type_description"),
      Icon: ListDashedOutlined,
    },
    {
      key: "NESTED_CHECKLIST",
      value: "NESTED_CHECKLIST",
      label: t("common:nested_checklist"),
      description: t("common:nested_checklist_ui_type_description"),
      Icon: ListDashedSlantedOutlined,
    },
  ];
};

export const getPlannerElementSetPropertyLabel = ({ t, property }) => {
  const PLANNER_ELEMENT_SET_PROPERTY_LABEL = {
    allowDescription: t("common:descriptor"),
    allowIcon: t("common:icon"),
    allowTags: t("common:tags"),
    allowGrades: t("common:grades"),
    allowCodes: t("common:code_other"),
  };

  return PLANNER_ELEMENT_SET_PROPERTY_LABEL[property];
};

export const getPlannerElementSetSelectionTypeOptions = ({ t }) => {
  return [
    {
      key: "SINGLE_SELECT",
      value: "SINGLE_SELECT",
      label: t("common:single_select"),
    },
    {
      key: "MULTI_SELECT",
      value: "MULTI_SELECT",
      label: t("common:multi_select"),
    },
  ];
};

export const filterIds = {
  searchText: "SEARCH_TEXT",
  grades: "GRADES",
  tags: "TAGS",
};

const columnIds = {
  label: "LABEL",
  descriptor: "DESCRIPTOR",
  icon: "ICON",
  grades: "GRADES",
  code: "CODE",
  tags: "TAGS",
};

export const bulkUpdateColumnIds = {
  grades: "GRADES",
  tags: "TAGS",
};

export const getPlannerElementSetConfig = ({ propertyValues }) => {
  const { uiType, optionalComponents, selectionType } = propertyValues;

  const optionalComponentsProperties = _.map(
    optionalComponents,
    propertyName => ({
      config: propertyName,
      value: true,
    })
  );

  let plannerElementSetConfig = [...optionalComponentsProperties];

  if (uiType === "CARD") {
    const selectionTypeProperty = {
      config: "selectionType",
      value: selectionType,
    };

    plannerElementSetConfig = update(plannerElementSetConfig, {
      $push: [selectionTypeProperty],
    });
  }

  return plannerElementSetConfig;
};

export const MAX_SAFARI_FILE_NAME_LENGTH = 130;

const TEMPLATE_FIELD_CONFIG_FILTERS = {
  properties: ["label"],
};

const getTemplateFieldPropertyValuesFilters = ({
  locale,
  curriculumProgramId,
}) => {
  return {
    curriculumProgramId,
    configLevel: [
      {
        type: "DEFAULT",
      },
    ],
    ...(ACLStore.can(ENABLE_DYNAMIC_LOCALISATION_OF_TEMPLATE)
      ? { locale }
      : {}),
  };
};

// Planner element set query variables
export const getPlannerElementSetQueryVariables = ({
  curriculumProgramId,
  locale,
}) => {
  return {
    templateFieldFilters: {
      curriculumProgramId,
    },
    templateFilters: {
      curriculumProgramId,
    },
    templateFieldConfigFilters: TEMPLATE_FIELD_CONFIG_FILTERS,
    templateFieldPropertyValuesFilters: getTemplateFieldPropertyValuesFilters({
      locale,
      curriculumProgramId,
    }),
  };
};

// Planner element set list query variables
export const getPlannerElementSetsQueryVariables = ({
  curriculumProgramId,
  curriculumNodeInterfaceType,
  locale,
}) => {
  return {
    id: curriculumProgramId,
    curriculumNodeInterfaceType,
    category: ["CUSTOM"],
    ...getPlannerElementSetQueryVariables({ curriculumProgramId, locale }),
  };
};

export const getSortedPropertyValues = ({
  values,
  optionalComponentsProperties,
}) => {
  // optionalComponentsProperties is already sorted
  const properties = _.filter(optionalComponentsProperties, ({ config }) =>
    _.includes(values, config)
  );
  return _.map(properties, ({ config }) => config);
};

export const injectGradesInNodes = ({ nodes }) => {
  return _.map(nodes, node => {
    const { associatedParents = [] } = node;
    const grades = _.filter(
      associatedParents,
      ({ __typename }) => __typename === "Grade"
    );
    return update(node, {
      grades: { $set: grades },
    });
  });
};

const _createDummyRootNode = _.memoize(
  plannerElementSetDetails => {
    const { label, type, nodes } = plannerElementSetDetails;
    const nodesHavingDepth1 = _.filter(nodes, { depth: 1 });

    return {
      id: -1,
      name: label,
      depth: 0,
      type,
      children: _.map(nodesHavingDepth1, "id"),
      isDummy: true,
    };
  },
  params => JSON.stringify(params)
);

const _getNodesHavingDepth1 = _.memoize(
  nodes => _.map(_.filter(nodes, { depth: 1 }), "id"),
  params => JSON.stringify(params)
);

/**
 * Table editor will always need one rootNode in order to function properly.
 * So, here we are creating it and updating parent-child relationship of nodes having depth 1
 */
export const formatPlannerElementNodes = _.memoize(
  plannerElementSetDetails => {
    const { nodes } = plannerElementSetDetails;

    const dummyRootNode = _createDummyRootNode(plannerElementSetDetails);
    const nodesHavingDepth1 = _getNodesHavingDepth1(nodes);

    const updatedNodes = _.map(nodes, node => {
      let updatedNode = _.cloneDeep(node);

      if (_.includes(nodesHavingDepth1, node.id)) {
        updatedNode = update(updatedNode, {
          parentId: { $set: dummyRootNode.id },
        });
      }

      if (_.isNull(updatedNode.children)) {
        updatedNode = update(updatedNode, {
          children: { $set: [] },
        });
      }

      return updatedNode;
    });

    return { nodes: [dummyRootNode, ...updatedNodes], rootNode: dummyRootNode };
  },
  params => JSON.stringify(params)
);

export const removeDummyRootNodeFromNodes = ({ nodes, rootNode }) => {
  const properNodes = _.filter(nodes, ({ id }) => id !== rootNode.id);

  return _.map(properNodes, node => {
    if (node.parentId === rootNode.id) {
      return update(node, {
        parentId: { $set: null },
      });
    }
    return node;
  });
};

/**------Beginning of utils to derive node editor config ----- */
const PLANNER_ELEMENT_SET_PROPERTIES_AND_NODE_EDITOR_COLUMNS_CONFIG_MAPPING = {
  CARD: {
    allowDescription: {
      columnId: columnIds.descriptor,
      columnLabel: "common:descriptor",
      nodeProperty: "subText",
      isRequired: true,
      errorMessage: "scopeAndSequence:descriptor_is_missing",
      placeholderTexts: ["common:descriptor"],
      style: { flex: 1.3 },
    },
    allowIcon: {
      columnId: columnIds.icon,
      columnLabel: "common:icon",
      nodeProperty: "icon",
      isRequired: true,
      errorMessage: "scopeAndSequence:icon_is_missing",
      style: { flex: 0.5 },
    },
  },
  NESTED_CHECKLIST: {
    allowTags: {
      columnId: columnIds.tags,
      columnLabel: "common:tags",
      style: { flex: 0.2 },
      canFilterDataByThisColumn: true,
      filterId: filterIds.tags,
      isBulkUpdateAllowed: true,
      bulkUpdateColumnId: bulkUpdateColumnIds.tags,
    },
    allowCodes: {
      columnId: columnIds.code,
      columnLabel: "common:code",
      style: { flex: 0.1 },
      placeholderTexts: ["common:code"],
    },
    allowGrades: {
      columnId: columnIds.grades,
      columnLabel: "common:grades",
      style: { flex: 0.2 },
      nodeProperty: "grades",
      isRequired: true,
      errorMessage: "scopeAndSequence:grades_are_missing",
      canFilterDataByThisColumn: true,
      filterId: filterIds.grades,
      isBulkUpdateAllowed: true,
      bulkUpdateColumnId: bulkUpdateColumnIds.grades,
    },
  },
  NESTED_CHECKLIST_UNIT_PLAN: {
    allowTags: {
      columnId: columnIds.tags,
      columnLabel: "common:tags",
      style: { flex: 0.2 },
      canFilterDataByThisColumn: true,
      filterId: filterIds.tags,
    },
    allowCodes: {
      columnId: columnIds.code,
      columnLabel: "common:code",
      style: { flex: 0.1 },
      placeholderTexts: ["common:code"],
    },
    allowGrades: {
      columnId: columnIds.grades,
      columnLabel: "common:grades",
      style: { flex: 0.2 },
      nodeProperty: "grades",
      canFilterDataByThisColumn: true,
      filterId: filterIds.grades,
    },
  },
  NESTED_CHECKLIST_MULTI_WINDOW_TABLE: {
    allowTags: {
      columnId: columnIds.tags,
      columnLabel: "common:tags",
      style: { flex: 0.2 },
      canFilterDataByThisColumn: true,
      filterId: filterIds.tags,
    },
    allowCodes: {
      columnId: columnIds.code,
      columnLabel: "common:code",
      style: { flex: 0.1 },
      placeholderTexts: ["common:code"],
    },
    allowGrades: {
      columnId: columnIds.grades,
      columnLabel: "common:grades",
      style: { flex: 0.2 },
      nodeProperty: "grades",
      canFilterDataByThisColumn: true,
      filterId: filterIds.grades,
    },
  },
  NESTED_CHECKLIST_PREVIEW_DATASET: {
    allowTags: {
      columnId: columnIds.tags,
      columnLabel: "common:tags",
      style: { flex: 0.2 },
      canFilterDataByThisColumn: true,
      filterId: filterIds.tags,
    },
    allowCodes: {
      columnId: columnIds.code,
      columnLabel: "common:code",
      style: { flex: 0.1 },
      placeholderTexts: ["common:code"],
    },
    allowGrades: {
      columnId: columnIds.grades,
      columnLabel: "common:grades",
      style: { flex: 0.2 },
      nodeProperty: "grades",
      canFilterDataByThisColumn: true,
      filterId: filterIds.grades,
    },
  },
};

const _getInitialPlannerElementNodeEditorConfig = ({ setType }) => {
  switch (setType) {
    case "CARD": {
      return {
        columns: [
          {
            id: "LABEL",
            label: "common:card_title",
            customTextStyles: {
              ...fontStyle.medium,
              color: colors.textDefault,
              fontSize: "1.6rem",
              lineHeight: "2.4rem",
            },
            customAddIconTooltipLabel: "common:add",
            placeholderTexts: ["common:card_title"],
            style: { flex: 1 },
            isRequired: true,
          },
        ],
        errorCheckFields: ["label"],
        errorCheckFieldLabels: {
          label: "scopeAndSequence:card_title_is_missing",
        },
      };
    }
    case "CHECKLIST": {
      return {
        columns: [
          {
            id: "LABEL",
            label: "common:list_item_other",
            customTextStyles: {
              ...fontStyle.medium,
              color: colors.textDefault,
              fontSize: "1.6rem",
              lineHeight: "2.4rem",
            },
            customAddIconTooltipLabel: "common:add",
            placeholderTexts: ["common:list_item_one"],
            style: { flex: 1 },
            isRequired: true,
          },
        ],
        errorCheckFields: ["label"],
        errorCheckFieldLabels: {
          label: "common:list_item_is_missing",
        },
      };
    }
    case "NESTED_CHECKLIST": {
      return {
        columns: [
          {
            id: "LABEL",
            label: "common:list_item_other",
            customTextStyles: {
              ...fontStyle.medium,
              color: colors.textDefault,
              fontSize: "1.6rem",
              lineHeight: "2.4rem",
            },
            customAddIconTooltipLabel: "common:add",
            placeholderTexts: ["common:list_item_one"],
            style: { flex: 1 },
            variant: MULTI_LEVEL,
            isRequired: true,
          },
        ],
        errorCheckFields: ["label"],
        errorCheckFieldLabels: {
          label: "common:list_item_is_missing",
        },
        allowedFilters: [filterIds.searchText],
        allowedBulkActions: [],
        showSidebar: true,
        maxNodeDepthForSidebar: 1,
      };
    }
    case "NESTED_CHECKLIST_UNIT_PLAN": {
      return {
        columns: [
          {
            id: "SELECTABLE_LABEL",
            label: "common:list_item_other",
            customTextStyles: {
              ...fontStyle.medium,
              color: colors.textDefault,
              fontSize: "1.6rem",
              lineHeight: "2.4rem",
            },
            style: { flex: 1 },
            variant: MULTI_LEVEL,
          },
        ],
        allowedFilters: [filterIds.searchText],
        showSidebar: true,
        maxNodeDepthForSidebar: 1,
      };
    }
    case "NESTED_CHECKLIST_MULTI_WINDOW_TABLE": {
      return {
        columns: [
          {
            id: "SELECTABLE_LABEL",
            label: "common:list_item_other",
            customTextStyles: {
              ...fontStyle.medium,
              color: colors.textDefault,
              fontSize: "1.6rem",
              lineHeight: "2.4rem",
            },
            style: { flex: 1 },
            variant: MULTI_LEVEL,
          },
        ],
        allowedFilters: [filterIds.searchText],
        showSidebar: true,
        maxNodeDepthForSidebar: 1,
        tableStyle: {
          minWidth: "1000px",
        },
      };
    }
    case "NESTED_CHECKLIST_PREVIEW_DATASET": {
      return {
        columns: [
          {
            id: "PREVIEW_SELECTABLE_LABEL",
            label: "common:list_item_other",
            customTextStyles: {
              ...fontStyle.medium,
              color: colors.textDefault,
              fontSize: "1.6rem",
              lineHeight: "2.4rem",
            },
            style: { flex: 1 },
            variant: MULTI_LEVEL,
          },
        ],
        allowedFilters: [filterIds.searchText],
        showSidebar: true,
        maxNodeDepthForSidebar: 1,
      };
    }
    default: {
      return {
        columns: [],
        errorCheckFields: [],
        errorCheckFieldLabels: {},
      };
    }
  }
};

export const getPlannerElementNodeEditorConfig = ({ setType, setConfig }) => {
  const initialNodeEditorConfig = _getInitialPlannerElementNodeEditorConfig({
    setType,
  });

  const updatedNodeEditorConfig = _.reduce(
    setConfig,
    (result, propertyConfig) => {
      const { config, value, valueType } = propertyConfig;
      if (valueType === "boolean" && value === true) {
        const {
          columnId,
          columnLabel,
          nodeProperty,
          isRequired,
          errorMessage,
          placeholderTexts = [],
          style,
          canFilterDataByThisColumn = false,
          isBulkUpdateAllowed = false,
          bulkUpdateColumnId,
        } = PLANNER_ELEMENT_SET_PROPERTIES_AND_NODE_EDITOR_COLUMNS_CONFIG_MAPPING[
          setType
        ][config];

        return update(result, {
          columns: (columns = []) => [
            ...columns,
            {
              id: columnId,
              label: columnLabel,
              placeholderTexts,
              style,
              isRequired,
            },
          ],
          errorCheckFields: (errorCheckFields = []) =>
            isRequired ? [...errorCheckFields, nodeProperty] : errorCheckFields,
          errorCheckFieldLabels: (errorCheckFieldLabels = {}) =>
            isRequired
              ? { ...errorCheckFieldLabels, [nodeProperty]: errorMessage }
              : errorCheckFieldLabels,
          allowedFilters: (allowedFilters = []) => {
            return canFilterDataByThisColumn
              ? [...allowedFilters, columnId]
              : allowedFilters;
          },
          allowedBulkActions: (allowedBulkActions = []) => {
            return isBulkUpdateAllowed
              ? [...allowedBulkActions, bulkUpdateColumnId]
              : allowedBulkActions;
          },
        });
      }

      return result;
    },
    initialNodeEditorConfig
  );

  return updatedNodeEditorConfig;
};

/**----------- End of utils to derive node editor config ---------- */

/**----------- Common utils ---------- */

export const convertToFlatArray = nodes => {
  return _.isArray(nodes) ? nodes : _.values(nodes);
};
